import { PageCard } from "src/common/styles/styles"
import InnerLoader from "src/components/InnerLoader"
import { CustomerError } from "../state/types"
import { Customer } from "src/common/models/customer"
import { useNavigate, useParams } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import { SystemDescriptions } from "src/common/descriptions"
import { useEffect, useState } from "react"
import { RootState } from "src/state/reducer"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { GET_CUSTOMER_BY_ID_API, UPSERT_CUSTOMER_API } from "../state/actionTypes"
import { CustomerForm } from "../CustomerForm"
import { Loader } from "src/components/Loader"
import { CancelConfirmationModal } from "src/common/descriptions/components/cancelConfirmationModal"
import { ModalSuccess } from "src/components/ModalSuccess"
import { cleanCurrentCustomer, getCustomerByIdApi, upsertCustomerApi } from "../state/action"
import { Urls } from "src/common/urls"

interface ReduxProps {
    error?: CustomerError
    isGettingCustomer: boolean
    isUpsertingCustomer: boolean
    upsertCustomerSuccess: boolean
    currentCustomer?: Customer
}

export const CreateCustomer = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const descriptions = SystemDescriptions.PAGES.CUSTOMER.CREATE

    const { id: customerId } = useParams<{ id: string }>();

    const [modalConfirmationOpen, setModalConfirmationOpen] = useState<boolean>(false)
    const [modalCancelOpen, setModalCancelOpen] = useState<boolean>(false)
    const [formValues, setFormValues] = useState<Customer | undefined>()
    const [actionButtonClicked, setActionButtonClicked] = useState<boolean>(false)

    useEffect(() => {
        if (customerId) {
            dispatch(getCustomerByIdApi(Number(customerId)))
        } else {
            dispatch(cleanCurrentCustomer())
        }
    }, [customerId, dispatch])

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state?.customer?.error,
        isGettingCustomer: serviceLoading(state, [GET_CUSTOMER_BY_ID_API]),
        isUpsertingCustomer: serviceLoading(state, [UPSERT_CUSTOMER_API]),
        upsertCustomerSuccess: state.customer.upsertCustomerSuccess,
        currentCustomer: state.customer.currentCustomer,
    }))

    const onFinish = (customer: Partial<Customer>) => {
        setFormValues(customer as Customer)
        setModalConfirmationOpen(true)
    }

    const onCancel = () => {
        setModalCancelOpen(true)
    }

    const handleConfirmationAccept = () => {
        setModalConfirmationOpen(false)
        setActionButtonClicked(true)
        dispatch(upsertCustomerApi(formValues as Customer))
    }

    const handleCancelAccept = () => {
        navigate(Urls.FRONTEND.CUSTOMER.CUSTOMERS)
    }

    const renderModals = () => (
        <>
            <CancelConfirmationModal
                isVisible={modalConfirmationOpen}
                onAccept={handleConfirmationAccept}
                onCancel={() => {
                    setModalConfirmationOpen(false)
                }}
                title={descriptions.CONFIRM_MODAL.TITLE}
                okText={descriptions.CONFIRM_MODAL.ACTION_BUTTON}
                cancelText={descriptions.CONFIRM_MODAL.CANCEL_BUTTON}
                description={descriptions.CONFIRM_MODAL.DESCRIPTION}
            />
            <CancelConfirmationModal
                isVisible={modalCancelOpen}
                onAccept={handleCancelAccept}
                onCancel={() => { setModalCancelOpen(false) }}
                title={descriptions.CANCEL_MODAL.TITLE}
                okText={descriptions.CANCEL_MODAL.ACTION_BUTTON}
                cancelText={descriptions.CANCEL_MODAL.CANCEL_BUTTON}
                description={descriptions.CANCEL_MODAL.DESCRIPTION}
            />
            <ModalSuccess
                isVisible={(reduxProps.upsertCustomerSuccess) && actionButtonClicked}
                title={descriptions.SUCCESS_MODAL.TITLE}
                description={descriptions.SUCCESS_MODAL.DESCRIPTION}
                onPrimaryAction={handleCancelAccept}
                onSecondaryActiont={handleCancelAccept}
                primaryLabel={descriptions.SUCCESS_MODAL.PRIMARY_LABEL}
                secondaryLabel={descriptions.SUCCESS_MODAL.SECONDARY_LABEL}
                hideSecondaryButton={true}
            />
        </>
    )

    const renderLoader = () => (
        <Loader
            isVisible={reduxProps.isUpsertingCustomer}
            title={descriptions.MAIN_ACTION_TITLE}
            description={descriptions.MAIN_ACTION_DESCRIPTION}
        />
    )

    return (
        <>
            <PageCard size="small" title={descriptions.TITLE}>
                {
                    reduxProps.isGettingCustomer
                        ? <InnerLoader />
                        : <CustomerForm
                            onFinish={onFinish}
                            onCancel={onCancel}
                            initialValues={customerId ? reduxProps.currentCustomer : undefined}
                        />
                }
            </PageCard>
            {renderModals()}
            {renderLoader()}
        </>
    )
}
